
import { computed, defineComponent, onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { Apollo, Notify } from '@/core/services';
import { GET_WMS_SYNC_STATUS } from '@/modules/wms/graphql/Queries';
import { SYNC_WMS_DATA } from '@/modules/wms/graphql/Mutations';
import { useWms } from '@/modules/wms/helpers';

export default defineComponent({
    name: 'wms',
    setup() {
        const store = useStore();
        const i18n = useI18n();
        const { wmsManagesProducts } = useWms();
        const status = ref({ status: 'never' }) as Record<any, any>;
        let pollTimer: any = null;

        const busy = computed(() => ['queued', 'running'].includes(status.value.status));
        const statusColor = computed(() => {
            return { queued: 'warning', running: 'primary', finished: 'success', failed: 'danger' }[status.value.status] || 'secondary';
        });

        const formatDate = (value: string) => new Date(value).toLocaleString();

        // while a sync is queued / running, check again every few seconds
        const poll = (next: () => void) => {
            clearTimeout(pollTimer);
            if (busy.value) {
                pollTimer = setTimeout(next, 3000);
            }
        };

        const getStatus = () => {
            Apollo.query({ query: GET_WMS_SYNC_STATUS, fetchPolicy: 'network-only' })
                .then(({ data }) => {
                    status.value = JSON.parse(data.wms_sync_status);
                    poll(getStatus);
                })
                .catch(() => poll(getStatus));
        };

        const syncWmsData = () => {
            if (!wmsManagesProducts.value) {
                return;
            }
            if (busy.value) {
                Notify.error(i18n.t('message.WMS_SYNC_ALREADY_RUNNING'));
                return;
            }
            store.getters.appInstance.$messageBox
                .confirm(`${i18n.t('message.WMS_SYNC_CONFIRM')}?`, i18n.t('message.INFO'), {
                    confirmButtonText: i18n.t('message.OK'),
                    cancelButtonText: i18n.t('message.CANCEL'),
                    type: 'info'
                })
                .then(() => {
                    Apollo.mutate({ mutation: SYNC_WMS_DATA }).then(({ data }) => {
                        const response = JSON.parse(data.sync_wms_data);
                        status.value = response;
                        response.already_running ? Notify.error(i18n.t('message.WMS_SYNC_ALREADY_RUNNING')) : Notify.success(i18n.t('message.WMS_SYNC_STARTED'));
                        poll(getStatus);
                    });
                })
                .catch(() => null);
        };

        onMounted(getStatus);
        onUnmounted(() => clearTimeout(pollTimer));

        return {
            status,
            busy,
            statusColor,
            wmsManagesProducts,
            formatDate,
            syncWmsData
        };
    }
});
